/* Cart popout — slide-in mini-cart
 *
 * Truly seamless add-to-cart:
 *   - Popout opens instantly (~16ms), with cached cart state already filled in.
 *   - The new line is rendered immediately from card/PDP data, *as a real-
 *     looking line* (same template as the final row): title, image, price,
 *     condition. The footer subtotal and badge tick up in the same frame.
 *   - /cart/add.js fires in the background. Its response is merged into the
 *     local cart state, the same DOM node "promotes" from optimistic to real
 *     (no swap, no flicker), and quantity/remove controls activate.
 *   - /cart.js is *not* refetched on the hot path. A deferred reconciliation
 *     ~2.5s later catches any server-side adjustments (scripts, discounts).
 *
 * Entry points:
 *   - Header cart icon → open instantly from cache, refresh in background
 *   - Collection grid blue ATC → fast path via embedded variant id
 *   - Singles JS card ATC → fallback /products/{handle}.js (popout already open)
 *   - PDP submit → routed via window.CartPopout.add() from
 *     sections/main-product-high-variant.liquid
 */
(function () {
  'use strict';

  var SELECTORS = {
    popout: '[data-cart-popout]',
    closers: '[data-cart-popout-close]',
    body: '[data-cart-popout-body]',
    itemsList: '[data-cart-popout-items]',
    empty: '[data-cart-popout-empty]',
    status: '[data-cart-popout-status]',
    cartCount: '[data-cart-popout-cart-count]',
    cartTotal: '[data-cart-popout-cart-total]',
    headerCount: '[data-header-cart-count]',
    headerCartButton: '.site-header-cart--button',
    collectionATC: '.productitem--action-atc',
    productCard: '[data-product-item]',
    productForm: 'form[data-product-form]',
    productPageATC: '[data-product-atc]',
    lineTemplate: '[data-cart-popout-line-template]',
  };

  var state = {
    qtyTimers: {},
    lastFocus: null,
    lastCart: null,
    reconcileTimer: null,
    // Concurrent-op model: DOM is the source of truth, server responses are
    // just acks. pendingOps counts in-flight /cart/add and /cart/change calls.
    // While > 0 we never let a server response repaint the list — the
    // optimistic DOM is already what the user expects to see. When it drops
    // back to 0 we do a single /cart.js reconcile to catch any divergence.
    pendingOps: 0,
    ops: {},        // opId → { id, type, variantId?, pending? } for in-flight adds
    opCounter: 0,
  };

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function popout() { return $(SELECTORS.popout); }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function parsePriceToCents(str) {
    if (!str) return 0;
    var clean = String(str).replace(/[^0-9.]/g, '');
    if (!clean) return 0;
    var num = parseFloat(clean);
    if (isNaN(num)) return 0;
    return Math.round(num * 100);
  }

  function setStatus(msg, isError) {
    var el = $(SELECTORS.status);
    if (!el) return;
    el.textContent = msg || '';
    el.classList.toggle('cart-popout__status--error', !!isError);
  }

  function api(url, options) {
    var opts = options || {};
    opts.headers = Object.assign(
      { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      opts.headers || {}
    );
    if (opts.body && typeof opts.body !== 'string' && !(opts.body instanceof FormData)) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(opts.body);
    }
    return fetch(url, opts).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok) throw json;
        return json;
      });
    });
  }

  function getCart() { return api('/cart.js'); }

  function addItem(variantId, quantity) {
    return api('/cart/add.js', {
      method: 'POST',
      body: { id: variantId, quantity: quantity || 1 },
    });
  }

  function addFromFormData(formData) {
    return api('/cart/add.js', { method: 'POST', body: formData });
  }

  function changeByKey(key, quantity) {
    return api('/cart/change.js', {
      method: 'POST',
      body: { id: key, quantity: quantity },
    });
  }

  function imageUrl(src) {
    if (!src) return '';
    return src.indexOf('http') === 0 || src.indexOf('//') === 0 ? src : 'https:' + src;
  }

  function getHandleFromCard(card) {
    if (!card) return null;
    if (card.dataset.productHandle) return card.dataset.productHandle;
    var url = card.getAttribute('data-product-quickshop-url') || '';
    var match = url.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* --- Scrape source for optimistic line -------------------------- */

  function scrapeFromCard(card) {
    if (!card) return {};

    var titleEl = card.querySelector('.productitem--title a')
      || card.querySelector('.productitem--title')
      || card.querySelector('[data-product-page-link]');
    var title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : '';

    var imgEl = card.querySelector('.singles-filter--card-image-img')
      || card.querySelector('.productitem--image-primary img')
      || card.querySelector('[data-product-item-image] img');
    if (!imgEl) {
      var imgs = card.querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].closest('.productitem--set-badge')) { imgEl = imgs[i]; break; }
      }
    }
    var image = '';
    if (imgEl && imgEl.tagName === 'IMG') {
      image = imgEl.currentSrc || imgEl.src || imgEl.getAttribute('data-src') || '';
    }

    var priceEl = card.querySelector('.atc-button--price-inline .money')
      || card.querySelector('.productitem__price .money')
      || card.querySelector('.price__current .money');
    var price = priceEl ? priceEl.textContent.trim() : '';

    // Condition / card-number row — e.g. "088 · NM". Best optimistic stand-in
    // for the variant label until /cart/add.js returns the real variant_title.
    var variantEl = card.querySelector('.productitem--card-number');
    var variant = variantEl ? variantEl.textContent.trim().replace(/\s+/g, ' ') : '';

    var linkEl = card.querySelector('[data-product-page-link]');
    var url = linkEl ? linkEl.getAttribute('href') : (card.getAttribute('data-product-quickshop-url') || '');

    return {
      title: title,
      image: image,
      price: price,
      priceCents: parsePriceToCents(price),
      variant: variant,
      url: url,
    };
  }

  function scrapeFromPDP() {
    var titleEl = document.querySelector('.product-title')
      || document.querySelector('[data-product-title]')
      || document.querySelector('h1');
    var title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : '';

    var imgEl = document.querySelector('.flex-pdp-media-gallery img')
      || document.querySelector('.product-gallery img')
      || document.querySelector('[data-product-media] img')
      || document.querySelector('.product-images img');
    var image = imgEl ? (imgEl.currentSrc || imgEl.src || '') : '';

    var priceEl = document.querySelector('.product-price .money')
      || document.querySelector('[data-product-price] .money')
      || document.querySelector('.price__current .money')
      || document.querySelector('.flex-pdp-price .money');
    var price = priceEl ? priceEl.textContent.trim() : '';

    // Selected variant text — radio picker uses `.variant-option-value-name-selected`,
    // select picker uses `<option selected>`. Join multi-option products with " / ".
    var variantParts = [];
    var radioSelected = document.querySelectorAll('.variant-option-value-name-selected');
    for (var i = 0; i < radioSelected.length; i++) {
      var t = radioSelected[i].textContent.trim().replace(/\s+/g, ' ');
      if (t) variantParts.push(t);
    }
    if (variantParts.length === 0) {
      var sel = document.querySelectorAll('select[name^="options"], select[name*="[options]"]');
      for (var j = 0; j < sel.length; j++) {
        var opt = sel[j].options[sel[j].selectedIndex];
        if (opt && opt.value) variantParts.push(opt.text.trim());
      }
    }

    return {
      title: title,
      image: image,
      price: price,
      priceCents: parsePriceToCents(price),
      variant: variantParts.join(' / '),
      url: window.location.pathname,
    };
  }

  /* --- Line population (shared between optimistic + real) --------- */

  function populateLine(node, data) {
    // data: { key?, variant_id?, image, title, variant, quantity, line_price?, price?, url, preserveImage }
    if (data.key) node.dataset.lineKey = data.key;

    var img = $('[data-cart-popout-line-image]', node);
    if (img) {
      if (data.image) {
        var url = imageUrl(data.image);
        var existing = img.getAttribute('src') || '';
        // On optimistic→real promote we'd otherwise swap to the Shopify CDN URL
        // and reload the image (a visible 1-2s flash on slow connections).
        // The scraped image is already correct content, so keep it.
        var keep = data.preserveImage && existing;
        if (!keep && existing !== url) {
          img.src = url;
        }
        img.alt = data.title || '';
        img.style.display = '';
      } else {
        img.style.display = 'none';
      }
    }

    $$('[data-cart-popout-line-link]', node).forEach(function (a) {
      a.href = data.url || '#';
    });

    var titleEl = $('[data-cart-popout-line-title]', node);
    if (titleEl) titleEl.textContent = data.title || '';

    var variantEl = $('[data-cart-popout-line-variant]', node);
    if (variantEl) {
      var variantText = data.variant && data.variant !== 'Default Title' ? data.variant : '';
      variantEl.textContent = variantText;
      variantEl.style.display = variantText ? '' : 'none';
    }

    var qtyEl = $('[data-cart-popout-qty-input]', node);
    if (qtyEl) {
      var q = data.quantity != null ? String(data.quantity) : '1';
      if (qtyEl.value !== q) qtyEl.value = q;
    }

    var priceEl = $('[data-cart-popout-line-price]', node);
    if (priceEl) {
      if (typeof data.line_price === 'number') {
        priceEl.textContent = formatMoney(data.line_price);
      } else if (data.price) {
        priceEl.textContent = data.price;
      } else {
        priceEl.textContent = '';
      }
    }
  }

  /* --- Op tracking ------------------------------------------------ */

  function startOp(opData) {
    var id = ++state.opCounter;
    var op = Object.assign({ id: id, ended: false }, opData || {});
    state.ops[id] = op;
    state.pendingOps++;
    return op;
  }

  function endOp(op) {
    if (!op || op.ended) return;
    op.ended = true;
    delete state.ops[op.id];
    state.pendingOps--;
    if (state.pendingOps === 0) scheduleFinalReconcile();
  }

  // Pull canonical cart from server after all in-flight ops are done. Only
  // re-renders if the server's view diverges from our optimistic local state.
  function scheduleFinalReconcile() {
    if (state.reconcileTimer) clearTimeout(state.reconcileTimer);
    state.reconcileTimer = setTimeout(function () {
      state.reconcileTimer = null;
      if (state.pendingOps > 0) return;
      getCart().then(function (cart) {
        if (state.pendingOps > 0) return;
        var prev = state.lastCart;
        var diverged = !prev
          || prev.item_count !== cart.item_count
          || prev.total_price !== cart.total_price
          || (prev.items && prev.items.length !== cart.items.length);
        state.lastCart = cart;
        if (diverged) renderCart(cart);
      }).catch(function () { /* noop */ });
    }, 250);
  }

  /* --- Footer computation ----------------------------------------- */

  // Footer count + total is state.lastCart (real items) plus optimistic DOM
  // rows that haven't been promoted to real yet. Called whenever either side
  // changes — replaces the inline calculation that used to live in renderCart.
  function updateFooter() {
    var cart = state.lastCart || { item_count: 0, total_price: 0 };
    var count = cart.item_count || 0;
    var total = cart.total_price || 0;

    $$('[data-optimistic-line]').forEach(function (row) {
      var qty = parseInt(row.dataset.optimisticQty || '1', 10);
      var priceCents = parseInt(row.dataset.optimisticPriceCents || '0', 10);
      count += qty;
      total += priceCents * qty;
    });

    updateCount(count);
    var totalEl = $(SELECTORS.cartTotal);
    if (totalEl) totalEl.textContent = formatMoney(total);

    var list = $(SELECTORS.itemsList);
    var empty = $(SELECTORS.empty);
    if (list && empty) empty.hidden = list.children.length > 0;
  }

  /* --- Optimistic row lifecycle ----------------------------------- */

  function createOptimisticRow(data, opId, variantId) {
    var template = $(SELECTORS.lineTemplate);
    var node = template.content.firstElementChild.cloneNode(true);
    node.classList.add('cart-popout__item--optimistic');
    node.setAttribute('data-optimistic-line', '');
    node.dataset.opId = String(opId);
    if (variantId) node.dataset.optimisticVariantId = String(variantId);
    if (data.priceCents) node.dataset.optimisticPriceCents = String(data.priceCents);
    node.dataset.optimisticQty = String(data.quantity || 1);

    populateLine(node, {
      title: data.title || 'Adding to cart…',
      image: data.image,
      variant: data.variant,
      quantity: data.quantity || 1,
      price: data.price,
      url: data.url,
    });

    return node;
  }

  function promoteOptimisticRow(opId, addedLine) {
    var list = $(SELECTORS.itemsList);
    if (!list) return null;
    var row = list.querySelector('[data-op-id="' + opId + '"]');
    if (!row) return null;

    // If the user is re-adding a product that's already in the cart, Shopify
    // returns the existing line (merged quantity). Collapse our optimistic
    // row into the existing real row instead of producing a duplicate.
    var attr = 'data-line-key';
    var existing = list.querySelector('[' + attr + '="' + addedLine.key.replace(/"/g, '\\"') + '"]');
    if (existing && existing !== row) {
      if (row.parentNode) row.parentNode.removeChild(row);
      populateLine(existing, {
        key: addedLine.key,
        title: addedLine.product_title || addedLine.title || '',
        variant: addedLine.variant_title,
        quantity: addedLine.quantity,
        line_price: addedLine.line_price,
        url: addedLine.url,
        preserveImage: true,
      });
      return existing;
    }

    row.classList.remove('cart-popout__item--optimistic');
    row.removeAttribute('data-optimistic-line');
    delete row.dataset.optimisticVariantId;
    delete row.dataset.optimisticPriceCents;
    delete row.dataset.optimisticQty;
    delete row.dataset.opId;

    populateLine(row, {
      key: addedLine.key,
      image: addedLine.image,
      title: addedLine.product_title || addedLine.title || '',
      variant: addedLine.variant_title,
      quantity: addedLine.quantity,
      line_price: addedLine.line_price,
      url: addedLine.url,
      preserveImage: true,
    });
    return row;
  }

  function removeOptimisticRow(opId) {
    var list = $(SELECTORS.itemsList);
    if (!list) return;
    var row = list.querySelector('[data-op-id="' + opId + '"]');
    if (row && row.parentNode) row.parentNode.removeChild(row);
  }

  /* --- Render ---------------------------------------------------- */

  function updateCount(count) {
    $$(SELECTORS.cartCount).forEach(function (el) {
      var prev = el.textContent;
      var next = String(count);
      if (prev !== next) {
        el.textContent = next;
        el.classList.remove('is-bumping');
        // Force reflow to restart animation
        void el.offsetWidth;
        el.classList.add('is-bumping');
      }
    });

    $$(SELECTORS.headerCount).forEach(function (el) {
      var prev = el.getAttribute('data-header-cart-count') || '';
      var next = count > 0 ? String(count) : '';
      el.setAttribute('data-header-cart-count', next);
      el.classList.toggle('visible', count > 0);
      if (prev !== next && count > 0) {
        el.classList.remove('is-bumping');
        void el.offsetWidth;
        el.classList.add('is-bumping');
      }
    });
  }

  // Full re-render of the items list from a cart object. Used for initial
  // hydrate, error recovery, and the post-op reconcile — NOT for the hot
  // path during user actions (those use targeted DOM updates so we don't
  // disturb in-flight optimistic rows or steal focus).
  function renderCart(cart) {
    var p = popout();
    if (!p) return;

    var list = $(SELECTORS.itemsList, p);
    var template = $(SELECTORS.lineTemplate);

    // Preserve any in-flight optimistic rows — pendingOps may be 0 here
    // (reconcile path) but a new add could still be in flight if a user
    // clicked ATC mid-reconcile.
    var optimisticRows = $$('[data-optimistic-line]', list);

    var byKey = {};
    $$('[data-cart-popout-line]', list).forEach(function (row) {
      if (row.dataset.lineKey) byKey[row.dataset.lineKey] = row;
    });

    var newOrder = [];
    var hasItems = cart && cart.items && cart.items.length > 0;

    if (hasItems) {
      cart.items.forEach(function (item) {
        var row = byKey[item.key];
        var isNew = false;
        if (!row) {
          row = template.content.firstElementChild.cloneNode(true);
          isNew = true;
        }
        populateLine(row, {
          key: item.key,
          image: item.image,
          title: item.product_title || item.title || '',
          variant: item.variant_title,
          quantity: item.quantity,
          line_price: item.line_price,
          url: item.url,
        });
        if (isNew) {
          row.classList.remove('cart-popout__item--just-added');
          void row.offsetWidth;
          row.classList.add('cart-popout__item--just-added');
          (function (r) {
            setTimeout(function () { r.classList.remove('cart-popout__item--just-added'); }, 1200);
          })(row);
        }
        newOrder.push(row);
      });
    }

    while (list.firstChild) list.removeChild(list.firstChild);
    optimisticRows.forEach(function (row) { list.appendChild(row); });
    newOrder.forEach(function (row) { list.appendChild(row); });

    updateFooter();
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      state.lastCart = cart;
      renderCart(cart);
      return cart;
    });
  }

  // Merge a /cart/add.js line response into state.lastCart in-place. Pure
  // local accounting — never re-renders, never bumps a generation counter.
  function applyAddedLine(addedLine) {
    if (!state.lastCart) {
      state.lastCart = { items: [], item_count: 0, total_price: 0 };
    }
    var cart = state.lastCart;

    var matchedIdx = -1;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].key === addedLine.key) { matchedIdx = i; break; }
    }
    if (matchedIdx >= 0) {
      cart.items[matchedIdx] = addedLine;
    } else {
      cart.items.unshift(addedLine);
    }

    var count = 0;
    var total = 0;
    for (var j = 0; j < cart.items.length; j++) {
      count += cart.items[j].quantity;
      total += cart.items[j].line_price;
    }
    cart.item_count = count;
    cart.total_price = total;
    return cart;
  }

  /* --- Open / close --------------------------------------------- */

  function openPopout() {
    var p = popout();
    if (!p) return;
    if (p.classList.contains('is-open')) return;
    state.lastFocus = document.activeElement;
    p.setAttribute('aria-hidden', 'false');
    p.classList.add('is-open');
    document.body.classList.add('cart-popout-open');
    requestAnimationFrame(function () {
      var panel = p.querySelector('.cart-popout__panel');
      if (panel) panel.focus();
    });
  }

  function closePopout() {
    var p = popout();
    if (!p) return;
    p.setAttribute('aria-hidden', 'true');
    p.classList.remove('is-open');
    document.body.classList.remove('cart-popout-open');
    // Safety net: ensure no ATC button anywhere on the page is left stuck
    // in a loading/disabled state. Catches edge cases (bfcache, network
    // stalls, Empire double-handling) where the AJAX-side cleanup didn't run.
    // Safe to clear `disabled` here because a genuinely sold-out variant
    // wouldn't have been clickable to open the popout in the first place.
    var stuck = document.querySelectorAll(
      '.product-form--atc-button.is-loading, .product-form--atc-button.processing, ' +
      '.product-form--atc-button[disabled], ' +
      '.productitem--action-atc.is-loading, .productitem--action-atc.processing'
    );
    stuck.forEach(function (b) {
      b.classList.remove('is-loading');
      b.classList.remove('processing');
      b.removeAttribute('disabled');
    });
    Object.keys(state.qtyTimers).forEach(function (k) {
      clearTimeout(state.qtyTimers[k]);
      delete state.qtyTimers[k];
    });
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      try { state.lastFocus.focus(); } catch (e) { /* noop */ }
    }
  }

  /* --- Add core --------------------------------------------------- */

  function performAdd(opts) {
    // opts: { variantId?, quantity?, formData?, optimistic, sourceButton? }
    var sourceButton = opts.sourceButton;
    var variantId = opts.variantId || null;
    var quantity = opts.quantity || 1;
    var optimistic = opts.optimistic || {};

    setStatus('');
    if (sourceButton) {
      sourceButton.classList.add('is-loading');
      // Empire's legacy AddToCartFlyout (empire.js) also listens to the form
      // submit and adds 'processing' to the ATC button. Because we preventDefault
      // its AJAX never reconciles, leaving the spinner stuck. Belt-and-suspenders:
      // strip it here, and again on success/error.
      sourceButton.classList.remove('processing');
    }

    // Each add is its own tracked op. No global busy lock — concurrent adds
    // are fine; each owns its own optimistic row identified by op.id.
    var op = startOp({ type: 'add', variantId: variantId });

    var row = createOptimisticRow(
      Object.assign({}, optimistic, { quantity: quantity }),
      op.id,
      variantId
    );
    var list = $(SELECTORS.itemsList);
    if (list) list.insertBefore(row, list.firstChild);
    updateFooter();
    openPopout();

    // Once the popout is up, the optimistic row already tells the user the
    // click registered — there's no reason to keep the source button in a
    // loading state until the AJAX reconciles. Clearing here guarantees the
    // button is re-clickable even if the network request stalls or fails
    // silently in a way .catch can't observe (e.g. bfcache nav).
    if (sourceButton) {
      sourceButton.classList.remove('is-loading');
      sourceButton.classList.remove('processing');
    }

    // Confirmation flash so the user immediately sees that *this* click was
    // registered, even mid-burst.
    void row.offsetWidth;
    row.classList.add('cart-popout__item--just-added');
    setTimeout(function () { row.classList.remove('cart-popout__item--just-added'); }, 1200);

    var addPromise = opts.formData
      ? addFromFormData(opts.formData)
      : (variantId ? addItem(variantId, quantity) : Promise.reject(new Error('Could not resolve variant.')));

    return addPromise
      .then(function (addedLine) {
        applyAddedLine(addedLine);
        var promoted = promoteOptimisticRow(op.id, addedLine);
        var pending = op.pending;
        endOp(op);
        if (sourceButton) {
          sourceButton.classList.remove('is-loading');
          sourceButton.classList.remove('processing');
        }

        // Replay any qty/remove the user fired while this add was in flight.
        // If they clicked remove, the DOM row was already detached, so we
        // can't call applyLineQty (it needs a row) — fire the server change
        // directly with the now-known line key.
        if (pending) {
          var targetQty = pending.type === 'remove' ? 0 : pending.value;
          if (promoted) {
            applyLineQty(promoted, targetQty);
          } else {
            locallyUpdateLine(addedLine.key, targetQty);
            updateFooter();
            syncLineQty(addedLine.key, targetQty, { instant: targetQty === 0 });
          }
        }

        updateFooter();
        return { response: addedLine, cart: state.lastCart };
      })
      .catch(function (err) {
        removeOptimisticRow(op.id);
        endOp(op);
        updateFooter();
        if (sourceButton) {
          sourceButton.classList.remove('is-loading');
          sourceButton.classList.remove('processing');
        }
        var msg = (err && (err.description || err.message)) || 'Could not add to cart.';
        setStatus(msg, true);
        throw err;
      });
  }

  /* --- Header cart icon intercept ------------------------------- */

  function handleHeaderCartClick(e) {
    var btn = e.target.closest && e.target.closest(SELECTORS.headerCartButton);
    if (!btn) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;

    e.preventDefault();
    e.stopPropagation();

    setStatus('');
    if (state.lastCart) renderCart(state.lastCart);
    openPopout();
    refreshCart().catch(function () {
      window.location.href = btn.getAttribute('href') || '/cart';
    });
  }

  /* --- Collection grid ATC intercept ---------------------------- */

  function handleCollectionATCClick(e) {
    var atc = e.target.closest && e.target.closest(SELECTORS.collectionATC);
    if (!atc) return;
    if (atc.classList.contains('disabled') || atc.getAttribute('aria-disabled') === 'true') return;

    var card = atc.closest(SELECTORS.productCard);
    if (!card) return;

    e.preventDefault();
    e.stopPropagation();

    var optimistic = scrapeFromCard(card);
    var embeddedVariantId = card.dataset.firstAvailableVariantId
      ? parseInt(card.dataset.firstAvailableVariantId, 10)
      : null;

    // Fast path: variant id is embedded on the card.
    if (embeddedVariantId) {
      performAdd({
        variantId: embeddedVariantId,
        quantity: 1,
        optimistic: optimistic,
        sourceButton: atc,
      }).catch(function () { /* status set */ });
      return;
    }

    // Fallback: resolve variant via /products/{handle}.js (singles JS cards).
    var handle = getHandleFromCard(card);
    if (!handle) return;

    // Wrap variant lookup + add in a single op so the loading state and
    // concurrent guarantees are the same as the fast path.
    setStatus('');
    atc.classList.add('is-loading');

    var op = startOp({ type: 'add' });
    var row = createOptimisticRow(
      Object.assign({}, optimistic, { quantity: 1 }),
      op.id
    );
    var list = $(SELECTORS.itemsList);
    if (list) list.insertBefore(row, list.firstChild);
    updateFooter();
    openPopout();

    void row.offsetWidth;
    row.classList.add('cart-popout__item--just-added');
    setTimeout(function () { row.classList.remove('cart-popout__item--just-added'); }, 1200);

    api('/products/' + handle + '.js')
      .then(function (product) {
        var variant = null;
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].available) { variant = product.variants[i]; break; }
        }
        if (!variant) throw new Error('Product is sold out.');
        row.dataset.optimisticVariantId = String(variant.id);
        return addItem(variant.id, 1);
      })
      .then(function (addedLine) {
        applyAddedLine(addedLine);
        var promoted = promoteOptimisticRow(op.id, addedLine);
        var pending = op.pending;
        endOp(op);
        atc.classList.remove('is-loading');

        if (pending && promoted) {
          if (pending.type === 'remove') applyLineQty(promoted, 0);
          else if (pending.type === 'qty') applyLineQty(promoted, pending.value);
        }
        updateFooter();
      })
      .catch(function (err) {
        removeOptimisticRow(op.id);
        endOp(op);
        updateFooter();
        atc.classList.remove('is-loading');
        var msg = (err && (err.description || err.message)) || 'Could not add to cart.';
        setStatus(msg, true);
      });
  }

  /* --- Product page form intercept (legacy product-form snippet) - */

  function handleProductFormSubmit(e) {
    var form = e.target;
    if (!form.matches(SELECTORS.productForm)) return;
    if (form.dataset.cartPopoutSkip === '1') return;

    e.preventDefault();
    // Stop Empire's AddToCartFlyout from also seeing this submit — otherwise
    // it fires a duplicate /cart/add and leaves 'processing' stuck on the
    // button. Requires this listener to be registered in capture phase.
    e.stopImmediatePropagation();

    var fd = new FormData(form);
    var atcBtn = form.querySelector(SELECTORS.productPageATC);
    var variantId = parseInt(fd.get('id'), 10) || null;
    var quantity = parseInt(fd.get('quantity'), 10) || 1;

    performAdd({
      variantId: variantId,
      quantity: quantity,
      formData: fd,
      optimistic: scrapeFromPDP(),
      sourceButton: atcBtn,
    }).catch(function () {
      // performAdd already showed the error via setStatus and cleaned up the
      // button state. Don't fall back to native submit — Empire's legacy
      // AddToCartFlyout would intercept it, fail too (same inventory error),
      // and leave the button with a stuck `disabled`/`processing` state that
      // can only be cleared by a page refresh.
      if (atcBtn) {
        atcBtn.classList.remove('is-loading');
        atcBtn.classList.remove('processing');
        atcBtn.removeAttribute('disabled');
      }
    });
  }

  /* --- Inside the popout ---------------------------------------- */

  function lineRow(target) {
    return target.closest('[data-cart-popout-line]');
  }

  // Mutate state.lastCart locally so the UI can re-render before the server
  // round-trip completes. Returns true if the line was found.
  function locallyUpdateLine(key, newQty) {
    if (!state.lastCart || !state.lastCart.items) return false;
    var idx = -1;
    for (var i = 0; i < state.lastCart.items.length; i++) {
      if (state.lastCart.items[i].key === key) { idx = i; break; }
    }
    if (idx < 0) return false;

    var line = state.lastCart.items[idx];
    if (newQty <= 0) {
      state.lastCart.items.splice(idx, 1);
    } else {
      var unitPrice = line.price;
      if (typeof unitPrice !== 'number' && line.quantity) {
        unitPrice = line.line_price / line.quantity;
      }
      line.quantity = newQty;
      if (typeof unitPrice === 'number') {
        line.line_price = Math.round(unitPrice * newQty);
      }
    }

    var count = 0, total = 0;
    for (var j = 0; j < state.lastCart.items.length; j++) {
      count += state.lastCart.items[j].quantity;
      total += state.lastCart.items[j].line_price || 0;
    }
    state.lastCart.item_count = count;
    state.lastCart.total_price = total;
    return true;
  }

  // Fire /cart/change.js in the background. The response is treated as an
  // ack — we never re-render from it. DOM was already updated optimistically
  // by applyLineQty. The reconcile that fires when pendingOps drops to 0 is
  // what catches any server-side divergence (inventory caps, discounts).
  function syncLineQty(key, qty, opts) {
    opts = opts || {};
    var delay = opts.instant ? 0 : 200;
    if (state.qtyTimers[key]) clearTimeout(state.qtyTimers[key]);
    state.qtyTimers[key] = setTimeout(function () {
      delete state.qtyTimers[key];
      var op = startOp({ type: 'change', key: key, qty: qty });
      changeByKey(key, qty)
        .then(function () {
          endOp(op);
        })
        .catch(function (err) {
          endOp(op);
          var msg = (err && (err.description || err.message)) || 'Could not update cart.';
          setStatus(msg, true);
          // Pull fresh state — our optimistic view is now wrong.
          refreshCart();
        });
    }, delay);
  }

  // Targeted DOM update for a real (non-optimistic) row. Updates just the
  // affected row and the footer — no full renderCart, so we don't disturb
  // other in-flight optimistic adds or steal focus from a qty input.
  function applyLineQty(row, newQty) {
    if (newQty < 0) newQty = 0;
    var key = row.dataset.lineKey;
    if (!key) return;

    if (newQty === 0) {
      if (row.parentNode) row.parentNode.removeChild(row);
    } else {
      var input = $('[data-cart-popout-qty-input]', row);
      if (input) input.value = newQty;
      if (state.lastCart && state.lastCart.items) {
        for (var i = 0; i < state.lastCart.items.length; i++) {
          if (state.lastCart.items[i].key === key) {
            var line = state.lastCart.items[i];
            var unitPrice = line.price;
            if (typeof unitPrice !== 'number' && line.quantity) {
              unitPrice = line.line_price / line.quantity;
            }
            if (typeof unitPrice === 'number') {
              var priceEl = $('[data-cart-popout-line-price]', row);
              if (priceEl) priceEl.textContent = formatMoney(Math.round(unitPrice * newQty));
            }
            break;
          }
        }
      }
    }

    locallyUpdateLine(key, newQty);
    updateFooter();
    syncLineQty(key, newQty, { instant: newQty === 0 });
  }

  function handleQtyClick(e) {
    var dec = e.target.closest('[data-cart-popout-qty-dec]');
    var inc = e.target.closest('[data-cart-popout-qty-inc]');
    var rem = e.target.closest('[data-cart-popout-line-remove]');

    if (!dec && !inc && !rem) return;
    var row = lineRow(e.target);
    if (!row) return;

    e.preventDefault();

    var input = $('[data-cart-popout-qty-input]', row);
    var current = parseInt(input && input.value, 10);
    if (isNaN(current)) current = 1;
    var next;
    if (rem) next = 0;
    else if (dec) next = current - 1;
    else next = current + 1;
    if (next < 0) next = 0;

    // Optimistic row (its /cart/add.js hasn't returned yet) — defer the
    // action and replay it in performAdd once we have the real line key.
    if (row.classList.contains('cart-popout__item--optimistic')) {
      var opId = parseInt(row.dataset.opId, 10);
      var op = state.ops[opId];
      if (op) {
        op.pending = next === 0 ? { type: 'remove' } : { type: 'qty', value: next };
      }
      // Reflect intent immediately in the DOM so the user gets feedback.
      if (next === 0) {
        if (row.parentNode) row.parentNode.removeChild(row);
      } else {
        if (input) input.value = next;
        row.dataset.optimisticQty = String(next);
        var priceCents = parseInt(row.dataset.optimisticPriceCents || '0', 10);
        var priceEl = $('[data-cart-popout-line-price]', row);
        if (priceEl && priceCents) priceEl.textContent = formatMoney(priceCents * next);
      }
      updateFooter();
      return;
    }

    applyLineQty(row, next);
  }

  function handleQtyInput(e) {
    var input = e.target;
    if (!input.matches('[data-cart-popout-qty-input]')) return;
    var row = lineRow(input);
    if (!row) return;
    if (row.classList.contains('cart-popout__item--optimistic')) return;
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 0) v = 1;

    var key = row.dataset.lineKey;
    if (!key) return;

    if (locallyUpdateLine(key, v)) {
      var priceEl = $('[data-cart-popout-line-price]', row);
      if (priceEl) {
        for (var i = 0; i < state.lastCart.items.length; i++) {
          if (state.lastCart.items[i].key === key) {
            priceEl.textContent = formatMoney(state.lastCart.items[i].line_price);
            break;
          }
        }
      }
      updateFooter();
    }
    syncLineQty(key, v);
  }

  function handleClose(e) {
    if (e.target.closest(SELECTORS.closers)) {
      e.preventDefault();
      closePopout();
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      var p = popout();
      if (p && p.classList.contains('is-open')) closePopout();
    }
  }

  /* --- Public API ----------------------------------------------- */

  window.CartPopout = {
    open: openPopout,
    close: closePopout,
    refresh: refreshCart,
    scrapeFromPDP: scrapeFromPDP,
    add: function (opts) { return performAdd(opts || {}); },
    // Kept for compatibility with the PDP submit handler. Concurrent adds
    // are now allowed; we never gate on a busy flag.
    isBusy: function () { return false; },
  };

  /* --- Wire up --------------------------------------------------- */

  function init() {
    if (!popout()) return;

    document.addEventListener('click', handleHeaderCartClick, true);
    document.addEventListener('click', handleCollectionATCClick, true);
    document.addEventListener('submit', handleProductFormSubmit, true);

    document.addEventListener('click', handleClose);
    document.addEventListener('click', handleQtyClick);
    document.addEventListener('input', handleQtyInput);
    document.addEventListener('keydown', handleKeydown);

    // Hydrate counts on first paint so the badge / popout footer
    // are accurate even before any interaction.
    refreshCart().catch(function () { /* noop */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
